import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route, Routes, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Container from '@mui/material/Container';
import { jwtDecode } from "jwt-decode";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LogRocket from 'logrocket';

import AuthLayout from './commons/AuthLayout';

import SignIn from './components/SignIn';
import Home from './components/Home';
import VerticalMenu from './components/Layout/VerticalMenu';
import AppBar from './components/Layout/AppBar';
import InvoiceDetail from './components/Invoices/InvoiceDetail';
import InvoiceBuilder from './components/Invoices/InvoiceBuilder';
import InvoiceList from './components/Invoices/InvoiceList';

import NewStaff from './components/Staffs/NewStaff';
import EditStaff from './components/Staffs/EditStaff';
import Staffs from './components/Staffs/Staffs';
import ChangePassword from './components/Staffs/ChangePassword';
import Products from './components/Products/Products';
import ListOrders from './components/Orders/Orders';
import Homepage from './components/Homepage';
import CreateProduct from './components/Products/CreateProduct';
import EditProduct from './components/Products/EditProduct';
import Customers from './components/Users-Customers/Users';
import NewUser from './components/Users-Customers/NewUser';
import AddOrder from './components/Orders/AddOrder';
import OrderDetail from './components/Orders/OrderDetail';
import OrderBankTransactionMatch from "./components/Orders/OrderBankTransactionMatch";
import PaidProof from './components/Orders/PaidProof';
import SendInvoice from './components/Orders/SendInvoice';
import Packing from './components/Packing/Packing';
import FullPackingVideo from './components/Packing/FullPackingVideo';
import PackingVideo from './components/Packing/PackingVideo';
import InventoryLevelTransactions from './components/Inventory/InventoryLevelTransactions';
import BankTransactionsList from "./components/BankTransactions/BankTransactionsList";
import BankTransactionImport from "./components/BankTransactions/BankTransactionImport";
import BankTransactionDetail from "./components/BankTransactions/BankTransactionDetail";
import AddInventoryLevelTransactions from './components/Inventory/AddInventoryLevelTransactions';
import UpdateInventoryLevelTransactions from './components/Inventory/UpdateInventoryLevelTransactions';
import ReportEmbed from './components/Inventory/ReportEmbed';
import OrderItemsReport from './components/OrderItems/OrderItemsReport';
import OrderStaffActivityReport from './components/Reports/OrderStaffActivityReport';
import OrderCountReport from './components/Reports/OrderCountReport';
import SubscriptionHealthDashboard from './components/Reports/SubscriptionHealthDashboard';
import MonthlyChurnReport from './components/Reports/MonthlyChurnReport';
import RevenueReport from './components/Reports/RevenueReport';
import UserDetails from './components/Users-Customers/UserDetails';
import CreatePackage from './components/Packages/CreatePackage';
import EditPackage from './components/Packages/EditPackage';
import ListPackages from './components/Packages/Packages';
import UserPackagesList from './components/UserPackages/UserPackages';
import UserPackageDetail from './components/UserPackages/UserPackageDetail';
import CreateUserPackage from './components/UserPackages/CreateUserPackage';
import CheckIn from './components/CheckIn/CheckIn';
import CheckInHistory from './components/CheckIn/CheckInHistory';
import EditCheckIn from './components/CheckIn/EditCheckIn';
import InactiveUsers from './components/Users-Customers/InactiveUsers';
import CreateSupplier from './components/Suppliers/CreateSupplier';
import EditSupplier from './components/Suppliers/EditSupplier';
import SuppliersList from './components/Suppliers/Suppliers';
import SupplierDetail from './components/Suppliers/SupplierDetail';
import CreatePurchaseOrder from './components/PurchaseOrders/CreatePurchaseOrder';
import EditPurchaseOrder from './components/PurchaseOrders/EditPurchaseOrder';
import PurchaseOrderList from './components/PurchaseOrders/PurchaseOrderList';
import PurchaseOrderDetail from './components/PurchaseOrders/PurchaseOrderDetail';
import StaffPOReceiving from './components/PurchaseOrders/StaffPOReceiving';
import StocktakeList from './components/Stocktakes/StocktakeList';
import StocktakeDetail from './components/Stocktakes/StocktakeDetail';
import StocktakeCountingScreen from './components/Stocktakes/StocktakeCountingScreen';
import QRScanner from './components/QRScanner/QRScanner';
import ScheduledAlertsList from './components/ScheduledAlerts/ScheduledAlertsList';
import CreateScheduledAlert from './components/ScheduledAlerts/CreateScheduledAlert';
import EditScheduledAlert from './components/ScheduledAlerts/EditScheduledAlert';
import DevTicketsBoard from './components/DevTickets/KanbanBoard';
import { WorkScheduleCalendar, MySchedule } from './components/WorkSchedule';
import ListMenus from './components/Menus/Menus';
import CreateMenu from './components/Menus/CreateMenu';
import EditMenu from './components/Menus/EditMenu';
import MenuPreview from './components/Menus/MenuPreview';
import MenuReport from './components/Menus/MenuReport';

import './App.scss';

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#0e2a04',
    }
  },
  typography: {
    fontFamily: [
      'Cabin',
      'Oxygen',
      'Roboto',
      'Arial',
    ].join(',')
  }
});


const App = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [controlRole, setControlRole] = useState();
  const [pageTitle, setPageTitle] = useState();
  const [openMenu, setOpenMenu] = useState();
  const [userId, setUserId] = useState();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      const userRole = decodedToken.role;
      const userId = decodedToken.id;
      const userEmail = decodedToken.email;
      const userName = decodedToken.name;
      if (userRole) {
        setControlRole(userRole);
      }
      if (userId) {
        setUserId(userId);
        LogRocket.identify(String(userId), {
          name: userName,
          role: userRole,
          email: userEmail,
        });
      }
    }
    if (!token) {
      setLoggedIn(false);
    } else {
      setLoggedIn(true);
    }
  }, [setLoggedIn]);

  const toggleMenu = (newOpen) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setOpenMenu(newOpen);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      {loggedIn && <VerticalMenu setLoggedIn={setLoggedIn} openMenu={openMenu} toggleMenu={toggleMenu} />}
      <Container
        sx={{
          pl: { xs: 0, sm: 0, md: loggedIn ? 31.25 : 0, lg: loggedIn ? 31.25 : 0 },
          pr: { xs: 0, sm: 0, md: 0, lg: 0 },
          m: { xs: 0, sm: 0, md: 0, lg: 0 },
        }}
        maxWidth="100%"
      >
        {loggedIn && <AppBar pageTitle={pageTitle} toggleMenu={toggleMenu} />}
        <div onClick={toggleMenu(false)}>
          <Routes>
            <Route element={<AuthLayout />}>
              <Route path='/' element={<Home />} />
              <Route path='/staffs' element={<Staffs key="staffs" setPageTitle={setPageTitle} controlRole={controlRole} navigate={navigate} />} />
              <Route path='/staffs/new' element={<NewStaff key="new_staff" setPageTitle={setPageTitle} controlRole={controlRole} />} />
              <Route path='/users' element={<Customers key="users" setPageTitle={setPageTitle} />} />
              <Route path='/users/new' element={<NewUser key="new_user" setPageTitle={setPageTitle} />} />
              <Route path='/staffs/edit/:id' element={<EditStaff key="edit_staff" setPageTitle={setPageTitle} controlRole={controlRole} />} />
              <Route path='/changepassword' element={<ChangePassword setPageTitle={setPageTitle} userId={userId} />} />
              <Route path='/products' element={<Products setPageTitle={setPageTitle} />} />
              <Route path='/createproduct' element={<CreateProduct setPageTitle={setPageTitle} />} />
              <Route path='/products/edit/:id' element={<EditProduct setPageTitle={setPageTitle} />} />
              <Route path='/orders' element={<ListOrders setPageTitle={setPageTitle} />} />
              <Route path='/customers' element={<Customers setPageTitle={setPageTitle} />} />
              <Route path='/customers/new' element={<NewUser setPageTitle={setPageTitle} />} />
              <Route path='/orders/new' element={<AddOrder setPageTitle={setPageTitle} />} />
              <Route path="/orders/:id" element={<OrderDetail />} />
              <Route path="/orders/:id/bank-match" element={<OrderBankTransactionMatch />} />
              <Route path="/invoices/:id/bank-match" element={<OrderBankTransactionMatch mode="invoice" />} />
              <Route path='/orders/:id/paid_proof' element={<PaidProof setPageTitle={setPageTitle} />} />
              <Route path='/orders/:id/send_invoice' element={<SendInvoice setPageTitle={setPageTitle} />} />
              <Route path="/invoices" element={<InvoiceList setPageTitle={setPageTitle} />} />
              <Route path="/invoices/new" element={<InvoiceBuilder setPageTitle={setPageTitle} />} />
              <Route path="/invoices/:id" element={<InvoiceDetail setPageTitle={setPageTitle} />} />
              <Route path='/packing' element={<Packing setPageTitle={setPageTitle} />} />
              <Route path='/full_packing_video' element={<FullPackingVideo setPageTitle={setPageTitle} />} />
              <Route path='/packing_video/:id' element={<PackingVideo setPageTitle={setPageTitle} />} />
              <Route path='/inventory_level_transactions' element={<InventoryLevelTransactions setPageTitle={setPageTitle} />} />
              <Route path='/order-items-report' element={<OrderItemsReport setPageTitle={setPageTitle} />} />
              <Route path='/order-staff-report' element={<OrderStaffActivityReport setPageTitle={setPageTitle} />} />
              <Route path='/order-count-report' element={<OrderCountReport setPageTitle={setPageTitle} />} />
              <Route path='/subscription-health' element={<SubscriptionHealthDashboard setPageTitle={setPageTitle} />} />
              <Route path='/monthly-churn' element={<MonthlyChurnReport setPageTitle={setPageTitle} />} />
              <Route path='/revenue-report' element={<RevenueReport setPageTitle={setPageTitle} />} />
              <Route path='/customers/:id' element={<UserDetails setPageTitle={setPageTitle} />} />
              <Route path='/inventory_level_transactions/new' element={<AddInventoryLevelTransactions setPageTitle={setPageTitle} />} />
              <Route path='/inventory_level_transactions/:id' element={<UpdateInventoryLevelTransactions setPageTitle={setPageTitle} />} />
              <Route path='/packages' element={<ListPackages setPageTitle={setPageTitle} />} />
              <Route path='/packages/new' element={<CreatePackage setPageTitle={setPageTitle} />} />
              <Route path='/packages/:id/edit' element={<EditPackage setPageTitle={setPageTitle} />} />
              <Route path='/user-packages' element={<UserPackagesList setPageTitle={setPageTitle} />} />
              <Route path='/user-packages/create' element={<CreateUserPackage setPageTitle={setPageTitle} />} />
              <Route path="/user-packages/:id" element={<UserPackageDetail setPageTitle={setPageTitle} />} />
              <Route path='/reports' element={<ReportEmbed setPageTitle={setPageTitle} />} />
              <Route path="/bank-transactions" element={<BankTransactionsList setPageTitle={setPageTitle} />} />
              <Route path="/bank-transactions/import" element={<BankTransactionImport setPageTitle={setPageTitle} />} />
              <Route path="/bank-transactions/:id" element={<BankTransactionDetail setPageTitle={setPageTitle} />} />
              <Route path="/check-in" element={<CheckIn />} />
              <Route path="/check-in-history" element={<CheckInHistory setPageTitle={setPageTitle} />} />
              <Route path="/check-in/edit/:checkInId" element={<EditCheckIn setPageTitle={setPageTitle} />} />
              <Route path="/users/inactive" element={<InactiveUsers setPageTitle={setPageTitle} />} />
              <Route path="/suppliers" element={<SuppliersList setPageTitle={setPageTitle} />} />
              <Route path="/suppliers/new" element={<CreateSupplier />} />
              <Route path="/suppliers/:id/edit" element={<EditSupplier />} />
              <Route path="/suppliers/:id" element={<SupplierDetail />} />
              <Route path="/purchase-orders" element={<PurchaseOrderList controlRole={controlRole} />} />
              <Route path="/purchase-orders/new" element={<CreatePurchaseOrder controlRole={controlRole} />} />
              <Route path="/purchase-orders/:id" element={<PurchaseOrderDetail controlRole={controlRole} />} />
              <Route path="/purchase-orders/:id/edit" element={<EditPurchaseOrder controlRole={controlRole} />} />
              <Route path="/purchase-orders/:id/receiving" element={<StaffPOReceiving controlRole={controlRole} />} />
              <Route path="/stocktakes" element={<StocktakeList setPageTitle={setPageTitle} controlRole={controlRole} />} />
              <Route path="/stocktakes/:stocktakeId" element={<StocktakeDetail setPageTitle={setPageTitle} controlRole={controlRole} />} />
              <Route path="/stocktakes/:stocktakeId/count" element={<StocktakeCountingScreen setPageTitle={setPageTitle} controlRole={controlRole} />} />
              <Route path="/qr-scanner" element={<QRScanner setPageTitle={setPageTitle} />} />
              <Route path="/scheduled-alerts" element={<ScheduledAlertsList setPageTitle={setPageTitle} />} />
              <Route path="/scheduled-alerts/new" element={<CreateScheduledAlert setPageTitle={setPageTitle} />} />
              <Route path="/scheduled-alerts/:id/edit" element={<EditScheduledAlert setPageTitle={setPageTitle} />} />
              <Route path="/dev-tickets" element={<DevTicketsBoard setPageTitle={setPageTitle} />} />
              <Route path="/menus" element={<ListMenus setPageTitle={setPageTitle} />} />
              <Route path="/menus/new" element={<CreateMenu setPageTitle={setPageTitle} />} />
              <Route path="/menus/:id/edit" element={<EditMenu setPageTitle={setPageTitle} />} />
              <Route path="/menus/:id/preview" element={<MenuPreview setPageTitle={setPageTitle} />} />
              <Route path="/menus-report" element={<MenuReport setPageTitle={setPageTitle} />} />
              <Route path="/work-schedules" element={<WorkScheduleCalendar setPageTitle={setPageTitle} controlRole={controlRole} />} />
              <Route path="/my-schedule" element={<MySchedule setPageTitle={setPageTitle} />} />
            </Route >
            <Route path='/login' element={<SignIn key="login" setLoggedIn={setLoggedIn} setControlRole={setControlRole} />} />
            <Route path='*' element={<Navigate to='/' />} />
            <Route path="homepage" element={<Homepage />} />
          </Routes >
        </div >
      </Container >
      <ToastContainer />
    </ThemeProvider >
  );
}

export default App;
